import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Grid, Box } from '@mui/material';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { resolveLogoSrc } from '../../utils/logoSrc';

interface EventImage {
    signedUrl: string;
    star?: number;
    order?: number;
}

interface EventGalleryProps {
    images: EventImage[];
}

function GalleryImage({
    src,
    alt,
    height,
    errorSource,
}: {
    src: string;
    alt: string;
    height: number;
    errorSource: string;
}) {
    const [hasError, setHasError] = React.useState(false);

    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            loading="eager"
            onError={
                hasError || !errorSource
                    ? undefined
                    : ({ currentTarget }) => {
                          currentTarget.src = errorSource;
                          setHasError(true);
                      }
            }
            sx={{ width: '100%', height, objectFit: 'contain', display: 'block' }}
        />
    );
}

const EventGallery: React.FC<EventGalleryProps> = ({ images }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { logoUrl } = usePublicSettings();
    const logoBucketUrl = import.meta.env.VITE_LOGO_BUCKET_URL ?? '';
    const errorSource = resolveLogoSrc(logoUrl, logoBucketUrl) ?? '';

    if (!images || images.length === 0) {
        return <></>;
    }

    const sorted = [...images].sort(
        (a, b) => (b.star ?? 0) - (a.star ?? 0) || (a.order ?? 0) - (b.order ?? 0),
    );

    if (isMobile) {
        return (
            <Grid container spacing={1} mb={3}>
                <Grid item xs={12}>
                    <GalleryImage
                        src={sorted[0].signedUrl}
                        alt="Event image 1"
                        height={200}
                        errorSource={errorSource}
                    />
                </Grid>
            </Grid>
        );
    }

    const display = sorted.slice(0, 5);
    const count = display.length;

    if (count === 1) {
        return (
            <Grid container spacing={1} mb={3}>
                <Grid item xs={12}>
                    <GalleryImage
                        src={display[0].signedUrl}
                        alt="Event image 1"
                        height={290}
                        errorSource={errorSource}
                    />
                </Grid>
            </Grid>
        );
    }

    if (count === 2) {
        return (
            <Grid container spacing={1} mb={3}>
                {display.map((img, i) => (
                    <Grid item xs={6} key={i}>
                        <GalleryImage
                            src={img.signedUrl}
                            alt={`Event image ${i + 1}`}
                            height={290}
                            errorSource={errorSource}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (count === 3) {
        return (
            <Grid container spacing={1} mb={3}>
                {display.map((img, i) => (
                    <Grid item xs={4} key={i}>
                        <GalleryImage
                            src={img.signedUrl}
                            alt={`Event image ${i + 1}`}
                            height={290}
                            errorSource={errorSource}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (count === 4) {
        return (
            <Grid container spacing={1} mb={3}>
                {display.map((img, i) => (
                    <Grid item xs={6} key={i}>
                        <GalleryImage
                            src={img.signedUrl}
                            alt={`Event image ${i + 1}`}
                            height={200}
                            errorSource={errorSource}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    }

    // count === 5: starred large on left, 4 thumbnails 2×2 on right
    return (
        <Grid container spacing={1} mb={3}>
            <Grid item xs={6}>
                <GalleryImage
                    src={display[0].signedUrl}
                    alt="Event image 1"
                    height={290}
                    errorSource={errorSource}
                />
            </Grid>
            <Grid item xs={6}>
                <Grid container spacing={1}>
                    {display.slice(1).map((img, i) => (
                        <Grid item xs={6} key={i}>
                            <GalleryImage
                                src={img.signedUrl}
                                alt={`Event image ${i + 2}`}
                                height={140}
                                errorSource={errorSource}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default EventGallery;
